import {
  EntityOperation,
  logEntityOperationFailure,
} from 'serviceWorker/helpers/entityFailureLog';
import { DomainMessagesTypes, Services } from 'shared/domain/messages/message';
import { broadcastPullFailed } from 'serviceWorker/helpers/broadcastPullError';
import { ENTITY_SERVICE_SYNC_DEBOUNCE_MS } from 'serviceWorker/const/syncTiming';
import hash from 'hash-sum';
import { IssueFormEntity } from 'serviceWorker/repository/issueForm/issueForm';
import { makeLoadedEventPublisher, readCollectionPayload } from 'serviceWorker/broadcasts/publishers';
import * as storedIssueForms from 'serviceWorker/db/issueForm/issueForm';
import * as storedIssueFormService from 'serviceWorker/db/issueForm/issueFormService';
import { createBoundedRunAfterSignal } from 'serviceWorker/events/boundedRunAfterSignal';
import { subscribeToAnySignal } from 'serviceWorker/events/swSignal';
import { swSignals } from 'serviceWorker/events/swSignals';
import { captureCollectionScope, type DataScope } from 'serviceWorker/helpers/dataScope';
import { debounce } from 'serviceWorker/helpers/debounce';
import { createConfigRetry, logWaitsForConfig } from 'serviceWorker/helpers/createConfigRetry';
import { debugLog } from 'shared/logger/debugLog';
import { LogLevel } from 'shared/types/logger';
import { resolveAuthenticatedConfig } from '../config';
import { createPullGuard } from '../factories/createPullGuard';
import { isSameAsPrev } from '../factories/isSameAsPrev';
import { PullStep, pullStepMarker } from '../factories/pullProgress';
import {
  createFinishedServiceData,
  hasRequiredConfig,
  Pull,
} from '../factories/types';
import { fetchIssueForm } from './fetchIssueForm';
import { IssueFormSkipReason } from './issueFormFetchResult';
import { issueFormContract } from 'shared/domain/issueForm/issueFormContract';

const prevUpdate = {
  current: {
    hashString: '',
    itemsString: '',
  },
};

const broadcastAllIssueFormData = makeLoadedEventPublisher(
  issueFormContract,
  DomainMessagesTypes.allIssueForm,
  () => readCollectionPayload(storedIssueFormService, storedIssueForms.get)
);

const pullGuard = createPullGuard(issueFormContract);

const markStep = pullStepMarker(issueFormContract);

const configRetry = createConfigRetry('sync:issueForms', () => scheduleIssueFormSyncFromApi());

/**
 * The download waits for the permissions to the project a few times: enough
 * for them to arrive after the worker started, not a loop for a user who has
 * no access to the project (that one gets the failure).
 */
const WAITS_FOR_PERMISSIONS = 2;

const permissionsWait = createBoundedRunAfterSignal(
  swSignals.permissionsStored,
  () => scheduleIssueFormSyncFromApi(),
  WAITS_FOR_PERMISSIONS
);

/** The download goes on once the permissions are stored, and they are pulled for it now; `false` when it gave up waiting. */
function waitForPermissions(): boolean {
  if (!permissionsWait.ask()) {
    return false;
  }
  markStep(PullStep.waitingForPermissions);
  swSignals.permissionsNeeded.emit();
  return true;
}

/** Readers waiting for the issue forms get the failure now, not at their timeout. */
async function publishDownloadFailure(error: Error): Promise<void> {
  markStep(PullStep.failed);
  const scope = captureCollectionScope(storedIssueFormService, undefined, Services.ISSUE_FORMS);
  await scope.write(() =>
    broadcastPullFailed({ channelContract: issueFormContract, entityName: Services.ISSUE_FORMS, error })
  );
}

/** The permissions will not come: the wait for them ends with the failure. */
async function failWaitForPermissions(): Promise<void> {
  if (!permissionsWait.isWaiting()) {
    return;
  }
  permissionsWait.cancel();
  await publishDownloadFailure(new Error('The permissions to the project could not be downloaded'));
}

/** The issue forms and their download state, written only while `scope` is current. */
function issueFormStorage(scope: DataScope) {
  return {
    issueForm: {
      get: scope.guard(storedIssueForms.get),
      clear: scope.guard(storedIssueForms.clear),
      quantity: scope.guard(storedIssueForms.quantity),
      updateBatch: scope.guard(storedIssueForms.updateBatch),
    },
    issueFormService: {
      get: scope.guard(storedIssueFormService.get),
      add: scope.guard(storedIssueFormService.add),
      reset: scope.guard(storedIssueFormService.reset),
    },
  };
}

const pullIssueForm: Pull = async (
  data,
  abortController,
  callerScope
): Promise<void> => {
  const scope = callerScope ?? captureCollectionScope(storedIssueFormService, data.projectId, Services.ISSUE_FORMS);
  const { issueForm, issueFormService } = issueFormStorage(scope);
  const publishIssueForms = (): Promise<void | undefined> => scope.write(broadcastAllIssueFormData);
  await fetchIssueForm(data, abortController)
    .then(async (result) => {
      if (!result.isDownloaded) {
        await issueFormService.add({
          total: undefined,
          isDownloading: false,
          isDownloaded: false,
          syncKey: undefined,
        });
        if (abortController.signal.aborted) {
          return;
        }
        // Not a failed download: the access to the project is not known yet (its permissions are
        // downloaded apart), so readers keep waiting and the download goes on once they are stored.
        if (result.skipReason === IssueFormSkipReason.permissionsNotStored && waitForPermissions()) {
          return;
        }
        await publishIssueForms();
        return;
      }

      permissionsWait.resetWaits();
      const { issueForms: response } = result;
      await issueFormService.add({
        total: response.length,
        isDownloading: true,
        isDownloaded: false,
      });
      const issueFormsEmpty = ((await issueForm.get()) ?? []).length === 0;

      const itemsString = JSON.stringify(response);
      const hashString = hash(itemsString);

      const currentUpdate = {
        hashString: hashString,
        itemsString: itemsString,
      };
      if (
        !isSameAsPrev(currentUpdate, prevUpdate.current) ||
        issueFormsEmpty
      ) {
        prevUpdate.current = currentUpdate;
        await issueForm.updateBatch(response);
      }

      if (abortController.signal.aborted) {
        return;
      }
      await issueFormService.add(createFinishedServiceData(response.length));
      await publishIssueForms();
      markStep(PullStep.ended);
    })
    .catch(async (e: unknown) => {
      logEntityOperationFailure(
        EntityOperation.FETCHING,
        { entityName: Services.ISSUE_FORMS, projectId: data.projectId, error: e },
        LogLevel.INFO
      );
      if (abortController.signal.aborted) {
        return;
      }
      markStep(PullStep.failed);
      await issueForm.clear();
      await issueFormService.add({
        total: undefined,
        isDownloading: false,
        isDownloaded: false,
        syncKey: undefined,
      });
      // Reads waiting for the first download end instead of timing out.
      await scope.write(() =>
        broadcastPullFailed({
          channelContract: issueFormContract,
          entityName: Services.ISSUE_FORMS,
          error: e,
        })
      );
    });
};

export const init = (): (() => void) => {
  const unsubscribes = [
    // The wait was for the permissions of the user or the project that are gone.
    subscribeToAnySignal([swSignals.loggedOut, swSignals.projectDataReplaced], permissionsWait.cancel),
    swSignals.permissionsUnavailable.subscribe(failWaitForPermissions),
  ];
  return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
};

const pullIssueFormHandler = async (): Promise<void> => {
  try {
    // Captured before the config is read: a pull decided under outdated data writes nothing.
    const scope = captureCollectionScope(storedIssueFormService, undefined, Services.ISSUE_FORMS);
    const { issueForm, issueFormService } = issueFormStorage(scope);
    markStep(PullStep.readingConfig);
    const { config: setup, reason } = await resolveAuthenticatedConfig();
    if (!setup) {
      markStep(PullStep.configUnavailable);
      logWaitsForConfig(Services.ISSUE_FORMS, reason, configRetry.wait(reason));
      return;
    }
    configRetry.reset();

    const status = await issueFormService.get();
    const hasRequired = hasRequiredConfig(setup, ['api', 'projectId']);
    const shouldPull = hasRequired && status?.isDownloaded !== true;

    if (shouldPull) {
      await pullGuard.execute(async (abortController) => {
        // The download is put over what is stored (maybe of another project); clearing also resets the download state.
        if (((await issueForm.quantity()) ?? 0) > 0) {
          await issueForm.clear();
        }
        await issueFormService.reset();
        await issueFormService.add({ isDownloading: true, isDownloaded: false });
        markStep(PullStep.started);
        await pullIssueForm(setup, abortController, scope);
      });
    } else if (status?.isDownloaded) {
      // Re-broadcast cached data when pull skipped but data already complete
      await scope.write(broadcastAllIssueFormData);
    }
  } catch (error) {
    debugLog('pullIssueFormHandler error:', error);
  }
};

/** Entry point of the issue form `sync` request (after the source debounce). */
export const scheduleIssueFormSyncFromApi = debounce(pullIssueFormHandler, ENTITY_SERVICE_SYNC_DEBOUNCE_MS);
